// Age Verification Page - Stripe Identity Integration
// Updated with InlineNotice for consistent error handling
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ShieldCheck, AlertCircle, Loader2 } from "lucide-react";
import { startAgeVerification, fetchAgeVerificationStatus } from "../api/ageVerification";
import { getVerifyReturnUrl } from "../utils/verificationNav";
import InlineNotice from "../components/common/InlineNotice";
import { useInlineNotice } from "../hooks/useInlineNotice";
import { useEntitlementsSafe } from "../context/EntitlementsContext";

export function VerifyAgePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [isVerified, setIsVerified] = useState(false);
  const [starting, setStarting] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Get auth token from localStorage for analytics
  const token = localStorage.getItem('token');
  
  // Refresh entitlements after verification
  const { refreshEntitlements } = useEntitlementsSafe();
  
  // Use InlineNotice hook for consistent error handling
  const { notice, showError, showSuccess, showInfo, dismissNotice } = useInlineNotice("verification", token);
  
  // Get return URL from query param
  const returnUrl = getVerifyReturnUrl(searchParams);

  const loadStatus = useCallback(async () => {
    try {
      const data = await fetchAgeVerificationStatus();
      setIsVerified(Boolean(data.is_age_verified));
      setStatus(data.identity_verification_status);
      
      // If newly verified, show success, refresh entitlements, and redirect
      if (data.is_age_verified && !isVerified) {
        showSuccess("You're now age verified! Redirecting...", { title: "Verification complete" });
        refreshEntitlements(); // Refresh to get updated verification status
        setTimeout(() => {
          navigate(returnUrl, { replace: true });
        }, 1500);
      }
      
      // Show info for pending status
      if (data.identity_verification_status === "pending") {
        showInfo("Your verification is being processed. This usually takes a few moments.", {
          title: "Verification pending",
        });
      }
    } catch (err) {
      console.error("Failed to load verification status:", err);
      showError(err, { customMessage: "Couldn't check your verification status." });
    } finally {
      setLoading(false);
    }
  }, [isVerified, navigate, returnUrl, showError, showSuccess, showInfo, refreshEntitlements]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  async function handleStart() {
    try {
      setStarting(true);
      dismissNotice(); // Clear any previous notice
      
      // Pass returnUrl to be included in Stripe's return_url
      const data = await startAgeVerification(returnUrl);
      if (data.verification_url) {
        window.location.href = data.verification_url;
      } else {
        showError(new Error("No verification URL"), { 
          customMessage: "Unable to start verification. Please try again." 
        });
      }
    } catch (err) {
      console.error("Failed to start verification:", err);
      const detail = err?.response?.data?.detail;
      
      if (detail === "ALREADY_VERIFIED") {
        showSuccess("You're already verified!", { title: "Already verified" });
        await loadStatus();
      } else if (detail === "VERIFICATION_IN_PROGRESS") {
        showInfo("A verification is already in progress. Please wait for it to complete.", {
          title: "Verification in progress",
        });
      } else {
        showError(err, { 
          customMessage: detail || "Couldn't start verification. Please try again.",
          onRetry: handleStart,
        });
      }
    } finally {
      setStarting(false);
    }
  }

  // Poll for status updates while pending
  useEffect(() => {
    if (!status || status === "verified" || isVerified) return;
    const id = setInterval(loadStatus, 5000);
    return () => clearInterval(id);
  }, [status, isVerified, loadStatus]);
  
  // Handle back button - go to return URL or previous page
  const handleBack = () => {
    navigate(returnUrl || -1);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-50">
        <div className="mx-auto flex h-full max-w-md flex-col px-4 py-4">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-pink-500" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-50">
      <div className="mx-auto max-w-md px-4 py-4">
        <div className="rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 shadow-sm p-5 sm:p-6">
      <div className="mb-6 flex items-center">
        <button
          type="button"
          onClick={handleBack}
          className="inline-flex items-center space-x-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800 transition"
          data-testid="verify-back-btn"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Back</span>
        </button>
      </div>
      
      <h1 className="mb-1 text-lg font-semibold text-slate-900 dark:text-slate-50 flex items-center gap-2">
        <ShieldCheck className="h-5 w-5 text-rose-600 dark:text-rose-400" />
        Verify Your Age
      </h1>
      <p className="mb-3 text-xs text-slate-600 dark:text-slate-300">
        To access certain features like After Dark lounge, Speed Dating, and
        high-value gifting, we need to confirm that you are 18+ using a secure
        ID check powered by Stripe.
      </p>

      {/* InlineNotice for all errors/success/info */}
      {notice && (
        <div className="mb-3">
          <InlineNotice
            variant={notice.variant}
            title={notice.title}
            actionLabel={notice.actionLabel}
            onAction={notice.onAction}
            href={notice.href}
            testId="verify-notice"
          >
            {notice.message}
          </InlineNotice>
        </div>
      )}

      {isVerified && (
        <div 
          className="mb-3 rounded-2xl bg-emerald-50 dark:bg-emerald-900/40 p-3 text-xs text-emerald-800 dark:text-emerald-100 border border-emerald-200 dark:border-emerald-500/30"
          data-testid="verify-success-banner"
        >
          <p className="font-semibold text-emerald-700 dark:text-emerald-200 flex items-center gap-2">
            <ShieldCheck className="h-4 w-4" />
            You&apos;re age verified
          </p>
          <p className="mt-1 text-[11px]">
            You now have access to all age-restricted features.
          </p>
          <p className="mt-2 text-[11px] text-emerald-600 dark:text-emerald-300">
            Redirecting you back...
          </p>
        </div>
      )}

      {!isVerified && (
        <div className="mb-3 rounded-2xl bg-slate-50 dark:bg-slate-900/80 p-3 text-xs text-slate-700 dark:text-slate-100 border border-slate-200 dark:border-slate-700">
          <p className="mb-1 text-[11px] font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-2">
            <AlertCircle className="h-3.5 w-3.5" />
            What to expect
          </p>
          <ul className="list-inside list-disc text-[11px] text-slate-600 dark:text-slate-300 space-y-1">
            <li>You&apos;ll upload a photo of your ID.</li>
            <li>You&apos;ll take a quick selfie to confirm it&apos;s you.</li>
            <li>We only use this to confirm you&apos;re 18+.</li>
          </ul>
        </div>
      )}

      {!isVerified && (
        <button
          disabled={starting}
          onClick={handleStart}
          className="mt-1 w-full rounded-full bg-rose-600 py-2 text-sm font-semibold text-white disabled:opacity-60 hover:bg-rose-700 transition flex items-center justify-center gap-2"
          data-testid="verify-start-btn"
        >
          {starting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Starting...
            </>
          ) : (
            <>
              <ShieldCheck className="h-4 w-4" />
              Start Verification
            </>
          )}
        </button>
      )}

      {status && !isVerified && (
        <p className="mt-2 text-[11px] text-slate-500 dark:text-slate-400" data-testid="verify-status">
          Current status:{" "}
          <span className="font-semibold text-slate-700 dark:text-slate-200">
            {status === "pending" && "Pending – checking in a moment..."}
            {status === "failed" && "Failed – please retry"}
            {status === "requires_input" && "Needs more info – please try again"}
          </span>
        </p>
      )}
      
      {/* Retry hint for failed status */}
      {status === "failed" && !isVerified && (
        <div className="mt-3 p-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-500/30">
          <p className="text-xs text-amber-800 dark:text-amber-200">
            <strong>Tip:</strong> Make sure your ID is clearly visible and the selfie matches.
            Common issues include blurry photos or mismatched faces.
          </p>
        </div>
      )}
        </div>
      </div>
    </div>
  );
}
